import React, { useState, useEffect } from "react";
import db from "../db/db"; // Import the IndexedDB instance
import ButtonTable from "../db/admin/ButtonTable";
import WidgetTable from "../db/admin/WidgetTable";
import UserTable from "../db/firebaseAdmin/UserTable";
import ItemTable from "../db/firebaseAdmin/ItemTable";
import ManageWidgets from "../db/firebaseAdmin/ManageWidgets";
import ManageButtons from "../db/firebaseAdmin/ManageButtons";
import CSVUploadItems from "../db/admin/CSVUploadItems";
import PageItemsTable from "../admin/PageItemsTable";
import PortalUsersTable from "../admin/PortalUsersTable";
import PortalRolesTable from "../admin/PortalRolesTable";
import PortalPagesTable from "../admin/PortalPagesTable";
import PortalFoldersTable from "../admin/PortalFoldersTable";
import "../css/AdminPage.css";

const PRIMARY_TABS = {
  USERS: "users",
  LOCAL_BUTTONS: "local-buttons",
  LOCAL_WIDGETS: "local-widgets",
  SERVER_BUTTONS: "server-buttons",
  SERVER_WIDGETS: "server-widgets",
  PORTAL_USERS: "portal-users",
  PORTAL_ROLES: "portal-roles",
  PORTAL_PAGES: "portal-pages",
  PORTAL_FOLDERS: "portal-folders",
  PORTAL_ITEMS: "portal-items",
  ADVANCED: "advanced",
};

const AdminPage = () => {
  const [buttonItems, setButtonItems] = useState([]);
  const [widgetItems, setWidgetItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [tempItem, setTempItem] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activePrimaryTab, setActivePrimaryTab] = useState(PRIMARY_TABS.USERS);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const buttonItemsList = await db.items.where("type").equals("button").toArray();
        const widgetItemsList = await db.items.where("type").equals("widget").toArray();

        setButtonItems(buttonItemsList);
        setWidgetItems(widgetItemsList);
      } catch (error) {
        console.error("Error fetching data: ", error);
        setError("Error fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Add new button item
  const handleAddButton = () => {
    const newButton = { id: Date.now(), title: "", type: "button" };
    setButtonItems([...buttonItems, newButton]);
    setEditingItem(newButton.id);
    setTempItem(newButton);
  };

  // Add new widget item
  const handleAddWidget = () => {
    const newWidget = { id: Date.now(), title: "", type: "widget" };
    setWidgetItems([...widgetItems, newWidget]);
    setEditingItem(newWidget.id);
    setTempItem(newWidget);
  };

  // Edit an item
  const handleEditClick = (item) => {
    setEditingItem(item.id);
    setTempItem(item);
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setTempItem({ ...tempItem, [field]: value });
  };

  // Save changes to an item
  const handleSaveChanges = async () => {
    const updatedButtonItems = buttonItems.map((item) => (item.id === tempItem.id ? tempItem : item));
    const updatedWidgetItems = widgetItems.map((item) => (item.id === tempItem.id ? tempItem : item));

    setButtonItems(updatedButtonItems);
    setWidgetItems(updatedWidgetItems);
    setEditingItem(null);
    // Optionally, update the database here
    await db.items.put(tempItem);
  };

  // Delete an item
  const handleDeleteItem = async (id) => {
    setButtonItems(buttonItems.filter((item) => item.id !== id));
    setWidgetItems(widgetItems.filter((item) => item.id !== id));
    await db.items.delete(id);
  };

  // Update item in Dexie
  const handleUpdateDexie = async (id) => {
    const item = buttonItems.find((item) => item.id === id);
    if (item) {
      await db.items.put(item);
    }
  };

  // Add item to Dexie
  const handleAddToDexie = async (id) => {
    const item = buttonItems.find((item) => item.id === id);
    if (item) {
      await db.items.add(item);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  const renderLocalButtonsSection = () => {
    return (
      <div className="admin-panel-section">
        <ButtonTable
          items={buttonItems}
          handleAddButton={handleAddButton}
          handleFieldChange={handleFieldChange}
          handleDeleteItem={handleDeleteItem}
          handleUpdateDexie={handleUpdateDexie}
          handleAddToDexie={handleAddToDexie}
          handleEditClick={handleEditClick}
          handleSaveChanges={handleSaveChanges}
          editingItem={editingItem}
          tempItem={tempItem}
          setEditingItem={setEditingItem}
        />
        <div className="admin-secondary-content-block">
          <CSVUploadItems />
        </div>
      </div>
    );
  };

  const renderLocalWidgetsSection = () => {
    return (
      <div className="admin-panel-section">
        <WidgetTable
          items={widgetItems}
          handleAddWidget={handleAddWidget}
          handleFieldChange={handleFieldChange}
          handleDeleteItem={handleDeleteItem}
          handleUpdateDexie={handleUpdateDexie}
          handleAddToDexie={handleAddToDexie}
          handleEditClick={handleEditClick}
          handleSaveChanges={handleSaveChanges}
          editingItem={editingItem}
          tempItem={tempItem}
          setEditingItem={setEditingItem}
        />
      </div>
    );
  };

  const renderPrimaryContent = () => {
    if (activePrimaryTab === PRIMARY_TABS.USERS) {
      return (
        <div className="admin-panel-section">
          <div className="admin-content-frame">
            <div className="user-table-container admin-full-width-panel">
              <UserTable />
            </div>
          </div>
        </div>
      );
    }

    if (activePrimaryTab === PRIMARY_TABS.LOCAL_BUTTONS) {
      return (
        <div className="admin-panel-section">
          <div className="admin-content-frame">
            {renderLocalButtonsSection()}
          </div>
        </div>
      );
    }

    if (activePrimaryTab === PRIMARY_TABS.LOCAL_WIDGETS) {
      return (
        <div className="admin-panel-section">
          <div className="admin-content-frame">
            {renderLocalWidgetsSection()}
          </div>
        </div>
      );
    }

    if (activePrimaryTab === PRIMARY_TABS.SERVER_BUTTONS) {
      return (
        <div className="admin-panel-section">
          <div className="admin-page-note">
            Manage server data for users to add to their grid. Changes below are saved in real time.
          </div>
          <div className="admin-content-frame">
            <ManageButtons />
          </div>
        </div>
      );
    }

    if (activePrimaryTab === PRIMARY_TABS.SERVER_WIDGETS) {
      return (
        <div className="admin-panel-section">
          <div className="admin-page-note">
            Manage server data for users to add to their grid. Changes below are saved in real time.
          </div>
          <div className="admin-content-frame">
            <ManageWidgets />
          </div>
        </div>
      );
    }

    if (activePrimaryTab === PRIMARY_TABS.PORTAL_USERS) {
      return (
        <div className="admin-panel-section">
          <div className="admin-page-note">
            Manage portal users and their roles. Changes are saved in real time to Firestore.
          </div>
          <div className="admin-content-frame">
            <PortalUsersTable />
          </div>
        </div>
      );
    }

    if (activePrimaryTab === PRIMARY_TABS.PORTAL_ROLES) {
      return (
        <div className="admin-panel-section">
          <div className="admin-page-note">
            Manage portal roles and permissions. Changes are saved in real time to Firestore.
          </div>
          <div className="admin-content-frame">
            <PortalRolesTable />
          </div>
        </div>
      );
    }

    if (activePrimaryTab === PRIMARY_TABS.PORTAL_PAGES) {
      return (
        <div className="admin-panel-section">
          <div className="admin-page-note">
            Manage portal pages and navigation. Changes are saved in real time to Firestore.
          </div>
          <div className="admin-content-frame">
            <PortalPagesTable />
          </div>
        </div>
      );
    }

    if (activePrimaryTab === PRIMARY_TABS.PORTAL_FOLDERS) {
      return (
        <div className="admin-panel-section">
          <div className="admin-page-note">
            Manage portal folders for organizing page items. Changes are saved in real time to Firestore.
          </div>
          <div className="admin-content-frame">
            <PortalFoldersTable />
          </div>
        </div>
      );
    }

    if (activePrimaryTab === PRIMARY_TABS.PORTAL_ITEMS) {
      return (
        <div className="admin-panel-section">
          <div className="admin-page-note">
            Manage portal page items. Changes are saved in real time to Firestore.
          </div>
          <div className="admin-content-frame">
            <PageItemsTable />
          </div>
        </div>
      );
    }

    return (
      <div className="admin-panel-section">
        <div className="admin-content-frame">
          <ItemTable />
        </div>
      </div>
    );
  };

  return (
    <div className="admin-page" style={{ paddingTop: "20px" }}>
      <div className="admin-header">
        <h2>Admin Management</h2>
      </div>
      <div className="admin-tab-bar">
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.USERS ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.USERS)}
        >
          Users
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.LOCAL_BUTTONS ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.LOCAL_BUTTONS)}
        >
          Local Buttons
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.LOCAL_WIDGETS ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.LOCAL_WIDGETS)}
        >
          Local Widgets
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.SERVER_BUTTONS ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.SERVER_BUTTONS)}
        >
          Server Buttons
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.SERVER_WIDGETS ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.SERVER_WIDGETS)}
        >
          Server Widgets
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.PORTAL_USERS ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.PORTAL_USERS)}
        >
          Portal Users
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.PORTAL_ROLES ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.PORTAL_ROLES)}
        >
          Portal Roles
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.PORTAL_PAGES ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.PORTAL_PAGES)}
        >
          Portal Pages
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.PORTAL_FOLDERS ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.PORTAL_FOLDERS)}
        >
          Portal Folders
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.PORTAL_ITEMS ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.PORTAL_ITEMS)}
        >
          Portal Items
        </button>
        <button
          type="button"
          className={`admin-tab-button ${activePrimaryTab === PRIMARY_TABS.ADVANCED ? "active" : ""}`}
          onClick={() => setActivePrimaryTab(PRIMARY_TABS.ADVANCED)}
        >
          Advanced
        </button>
      </div>
      <div className="admin-tab-panel">
        {renderPrimaryContent()}
      </div>
    </div>
  );
};

export default AdminPage;
